import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Layout from '../../components/Layout';
import SiteMap, { DEFAULT_CAMERAS } from './SiteMap';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import { useToast } from '../../context/ToastContext';

const VIDEO_WIDGETS: PageWidget[] = [
  { id:'live',  label:'Live View',     icon:'📺', description:'Camera grid with live or snapshot feeds', defaultVisible:true },
  { id:'map',   label:'Camera Map',    icon:'🗺️', description:'Interactive map showing camera locations', defaultVisible:true },
  { id:'views', label:'Manage Views',  icon:'⊞', description:'Create and manage multi-camera view layouts', defaultVisible:true },
];

// ─── Types ────────────────────────────────────────────────────────────────────

type LayoutType = '1x1' | '2x2' | '3x3' | '4x4' | '1+5' | '2+4';

interface DiscoveredCamera {
  id: string;
  name: string;
  location: string;            // integration label / site
  integrationLabel: string;
  type: string;                // 'dahua' | 'milestone' | 'rtsp' etc
  channel?: number;
  snapshotUrl?: string;
}

interface ViewCell {
  slot: number;
  cameraId: string | null;
}

interface CameraView {
  id: string;
  name: string;
  layout: LayoutType;
  cells: ViewCell[];
  createdAt: string;
}

const LAYOUT_META: Record<LayoutType, { label: string; cols: number; rows: number; total: number; icon: string }> = {
  '1x1': { label:'1×1 Single',  cols:1, rows:1, total:1,  icon:'⬜' },
  '2x2': { label:'2×2 Quad',    cols:2, rows:2, total:4,  icon:'⊞'  },
  '3x3': { label:'3×3 Nine',    cols:3, rows:3, total:9,  icon:'⊟'  },
  '4x4': { label:'4×4 Sixteen', cols:4, rows:4, total:16, icon:'⊠'  },
  '1+5': { label:'1 Main + 5',  cols:3, rows:2, total:6,  icon:'▣'  },
  '2+4': { label:'2 Main + 4',  cols:3, rows:2, total:6,  icon:'▤'  },
};

function makeEmptyCells(layout: LayoutType): ViewCell[] {
  return Array.from({ length: LAYOUT_META[layout].total }, (_, i) => ({ slot: i, cameraId: null }));
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

function useLocalStorage<T>(key: string, init: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : init; }
    catch { return init; }
  });
  useEffect(() => { localStorage.setItem(key, JSON.stringify(value)); }, [key, value]);
  return [value, setValue];
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

function getToken(): string {
  return localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token') || '';
}

// ─── Camera discovery from integrations ──────────────────────────────────────

function useIntegrationCameras(): { cameras: DiscoveredCamera[]; loading: boolean; refresh: () => void } {
  const [cameras, setCameras] = useState<DiscoveredCamera[]>([]);
  const [loading, setLoading] = useState(false);

  const discover = useCallback(async () => {
    setLoading(true);
    const discovered: DiscoveredCamera[] = [];

    // Load camera registry from API (Settings → Cameras tab)
    try {
      const tok = getToken();
      const res = await fetch('/api/integrations/cameras', {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.ok) {
        const data = await res.json();
        const regCams: { id: number; name: string; cameraId: string; ip: string; site: string; location: string; type: string; status: string }[] = data.cameras || [];
        for (const cam of regCams) {
          discovered.push({
            id: `reg-${cam.id}`,
            name: cam.name,
            location: `${cam.site} · ${cam.location}`,
            integrationLabel: cam.site,
            type: cam.status === 'Online' ? 'registry' : 'offline',
            snapshotUrl: undefined,
          });
        }
      }
    } catch {}

    try {
      const stored = localStorage.getItem('lemai_integrations');
      const integrations: {
        id: string; type: string; label: string; category: string;
        status: string; host: string; port: string;
        username?: string; password?: string;
        extra?: Record<string, string>;
      }[] = stored ? JSON.parse(stored) : [];

      const cctvIntegrations = integrations.filter(
        i => i.category === 'cctv' && (i.status === 'connected' || i.status === 'pending')
      );

      for (const integ of cctvIntegrations) {
        if (integ.type === 'dahua') {
          try {
            // Push credentials to backend first — the backend stores them in memory
            // and needs them before /channels or /snapshot will work
            await fetch('/api/dahua/configure', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getToken()}`,
              },
              body: JSON.stringify({
                host:     integ.host     || integ.extra?.host     || '',
                port:     integ.port     || integ.extra?.port     || '80',
                username: integ.username || integ.extra?.username || 'admin',
                password: integ.password || integ.extra?.password || '',
              }),
            });

            const res = await fetch('/api/dahua/channels', {
              headers: { Authorization: `Bearer ${getToken()}` },
            });

            if (res.ok) {
              const data = await res.json();
              // Backend returns { channel: N, name: "..." } — note field is 'channel' not 'id'
              const channels: { channel: number; name: string }[] = data.channels || [];

              if (channels.length > 0) {
                for (const ch of channels) {
                  discovered.push({
                    id: `${integ.id}-ch${ch.channel}`,
                    name: ch.name || `${integ.label} — CH${ch.channel}`,
                    location: integ.label,
                    integrationLabel: integ.label,
                    type: 'dahua',
                    channel: ch.channel,
                    snapshotUrl: `/api/dahua/snapshot?channel=${ch.channel}`,
                  });
                }
              } else {
                // NVR connected but returned 0 channels — add CH1–4 as fallback
                for (let i = 1; i <= 4; i++) {
                  discovered.push({
                    id: `${integ.id}-ch${i}`,
                    name: `${integ.label} — CH${i}`,
                    location: integ.label,
                    integrationLabel: integ.label,
                    type: 'dahua',
                    channel: i,
                    snapshotUrl: `/api/dahua/snapshot?channel=${i}`,
                  });
                }
              }
            } else {
              // Non-OK response — still show fallback channels so user can attempt
              for (let i = 1; i <= 4; i++) {
                discovered.push({
                  id: `${integ.id}-ch${i}`,
                  name: `${integ.label} — CH${i}`,
                  location: integ.label,
                  integrationLabel: integ.label,
                  type: 'dahua',
                  channel: i,
                  snapshotUrl: `/api/dahua/snapshot?channel=${i}`,
                });
              }
            }
          } catch {
            // Network error — still surface fallback channels
            for (let i = 1; i <= 4; i++) {
              discovered.push({
                id: `${integ.id}-ch${i}`,
                name: `${integ.label} — CH${i}`,
                location: integ.label,
                integrationLabel: integ.label,
                type: 'dahua',
                channel: i,
                snapshotUrl: `/api/dahua/snapshot?channel=${i}`,
              });
            }
          }
        } else if (integ.type === 'rtsp') {
          discovered.push({
            id: `${integ.id}-stream`,
            name: integ.extra?.host || integ.label,
            location: integ.label,
            integrationLabel: integ.label,
            type: 'rtsp',
            snapshotUrl: undefined,
          });
        } else {
          // Milestone, Genetec, ONVIF — show as placeholder until backend endpoints added
          discovered.push({
            id: `${integ.id}-main`,
            name: integ.label,
            location: integ.label,
            integrationLabel: integ.label,
            type: integ.type,
          });
        }
      }
    } catch {}

    setCameras(discovered);
    setLoading(false);
  }, []);

  useEffect(() => { discover(); }, [discover]);

  return { cameras, loading, refresh: discover };
}

// ─── Snapshot poller ──────────────────────────────────────────────────────────

function useSnapshotPoller(url: string, enabled: boolean, intervalMs = 1500): string {
  const [src, setSrc] = useState('');
  const urlRef = useRef(url);
  urlRef.current = url;

  useEffect(() => {
    if (!enabled || !url) { setSrc(''); return; }
    let active = true;
    let current = '';

    async function poll() {
      try {
        const res = await fetch(urlRef.current + '&_t=' + Date.now(), {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok || !active) return;
        const blob = await res.blob();
        if (!active) { URL.revokeObjectURL(current); return; }
        const next = URL.createObjectURL(blob);
        setSrc(prev => { URL.revokeObjectURL(prev); return next; });
        current = next;
      } catch {}
    }

    poll();
    const t = setInterval(poll, intervalMs);
    return () => { active = false; clearInterval(t); URL.revokeObjectURL(current); };
  }, [url, enabled, intervalMs]);

  return src;
}

// ─── Snapshot Cell ────────────────────────────────────────────────────────────

function SnapshotCell({
  camera, onFullscreen, fullscreen, onClose,
}: {
  camera: DiscoveredCamera | null;
  onFullscreen?: () => void;
  fullscreen?: boolean;
  onClose?: () => void;
}) {
  const hasSnap = !!(camera?.snapshotUrl);
  const src = useSnapshotPoller(camera?.snapshotUrl || '', hasSnap);
  const [fps, setFps] = useState(0);
  const frameCount = useRef(0);
  const lastSec = useRef(Date.now());

  useEffect(() => {
    if (!src) return;
    frameCount.current += 1;
    const now = Date.now();
    if (now - lastSec.current >= 1000) {
      setFps(frameCount.current);
      frameCount.current = 0;
      lastSec.current = now;
    }
  }, [src]);

  const now = new Date();
  const ts = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;

  return (
    <div style={{ position:'relative', background:'#000', borderRadius:0, overflow:'hidden', aspectRatio:'16/9', border: fullscreen ? 'none' : '1px solid #222' }}>
      {!camera ? (
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'8px', background:'#000' }}>
          <div style={{ width:'28px', height:'28px', border:'1px dashed #333', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', opacity:0.3 }}>+</div>
          <div style={{ fontSize:'9px', color:'#444', textTransform:'uppercase', letterSpacing:'0.06em' }}>Empty Slot</div>
        </div>
      ) : src ? (
        <img src={src} alt={camera.name} style={{ width:'100%', height:'100%', objectFit:'contain', display:'block', background:'#000' }} />
      ) : (
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'6px', background:'#000' }}>
          {hasSnap ? (
            <>
              <div style={{ width:'18px', height:'18px', border:'2px solid #222', borderTop:'2px solid #3b82f6', borderRadius:'50%', animation:'spin 1s linear infinite' }} />
              <div style={{ fontSize:'9px', color:'#444' }}>Connecting…</div>
            </>
          ) : (
            <>
              <div style={{ fontSize:'18px', opacity:0.15 }}>📹</div>
              <div style={{ fontSize:'9px', color:'#333' }}>{camera.name}</div>
            </>
          )}
        </div>
      )}

      {camera && (
        <>
          {/* REC indicator — top-left */}
          <div style={{ position:'absolute', top:'4px', left:'4px', display:'flex', alignItems:'center', gap:'3px', background:'rgba(0,0,0,0.6)', padding:'2px 4px' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:'#dc2626' }} />
            <span style={{ fontSize:'8px', fontWeight:700, color:'#fff', letterSpacing:'0.05em' }}>REC</span>
          </div>
          {/* Camera name — bottom-left */}
          <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'4px 6px', background:'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
            <div style={{ fontSize:'10px', color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:500 }}>{camera.name}</div>
          </div>
          {/* Timestamp — bottom-right */}
          <div style={{ position:'absolute', bottom:'4px', right:'4px', background:'rgba(0,0,0,0.5)', padding:'2px 4px' }}>
            <span style={{ fontSize:'9px', fontFamily:'monospace', color:'#ccc' }}>{ts}</span>
          </div>
          {/* Fullscreen / close controls */}
          {onFullscreen && !fullscreen && (
            <button onClick={e => { e.stopPropagation(); onFullscreen(); }}
              style={{ position:'absolute', top:'4px', right:'4px', background:'rgba(0,0,0,0.6)', border:'none', borderRadius:0, color:'white', fontSize:'10px', padding:'2px 5px', cursor:'pointer' }}>⛶</button>
          )}
          {onClose && (
            <button onClick={e => { e.stopPropagation(); onClose(); }}
              style={{ position:'absolute', top:'4px', right:'4px', background:'rgba(0,0,0,0.6)', border:'none', borderRadius:0, color:'white', fontSize:'10px', padding:'2px 5px', cursor:'pointer' }}>✕</button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Fullscreen ───────────────────────────────────────────────────────────────

function FullscreenViewer({ camera, onClose }: { camera: DiscoveredCamera; onClose: () => void }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'#000', zIndex:400, display:'flex', flexDirection:'column' }}>
      <div style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px', background:'#0d0d0d', borderBottom:'1px solid #1a1a1a' }}>
        <div style={{ fontWeight:700, fontSize:'15px', color:'white', flex:1 }}>{camera.name} — {camera.location}</div>
        <div style={{ fontSize:'11px', color:'#555555' }}>{camera.integrationLabel}</div>
        <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)', border:'none', borderRadius:'4px', color:'white', width:'32px', height:'32px', fontSize:'16px', cursor:'pointer' }}>✕</button>
      </div>
      <div style={{ flex:1, padding:'16px' }}>
        <SnapshotCell camera={camera} fullscreen onClose={onClose} />
      </div>
    </div>
  );
}

// ─── Searchable camera dropdown (portal-based, never clipped) ────────────────

function CameraSelect({
  cameras, value, onChange, loading,
}: {
  cameras: DiscoveredCamera[];
  value: string | null;
  onChange: (id: string | null) => void;
  loading: boolean;
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos]     = useState({ top: 0, left: 0, width: 0, above: false });
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLInputElement>(null);

  const selected = cameras.find(c => c.id === value);

  const filtered = query.trim()
    ? cameras.filter(c =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.integrationLabel.toLowerCase().includes(query.toLowerCase()) ||
        String(c.channel ?? '').includes(query)
      )
    : cameras;

  const filteredByInteg: Record<string, DiscoveredCamera[]> = {};
  filtered.forEach(c => {
    if (!filteredByInteg[c.integrationLabel]) filteredByInteg[c.integrationLabel] = [];
    filteredByInteg[c.integrationLabel].push(c);
  });

  function openDropdown() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropH = 360; // approximate dropdown height
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < dropH && rect.top > dropH;
    setPos({
      top:   above ? rect.top - dropH - 4 : rect.bottom + 4,
      left:  rect.left,
      width: rect.width,
      above,
    });
    setOpen(true);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current && !triggerRef.current.contains(target)) {
        // Check if click is inside the portal dropdown
        const portal = document.getElementById('cam-select-portal');
        if (portal && portal.contains(target)) return;
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  function select(id: string | null) {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  const dropdown = open ? ReactDOM.createPortal(
    <div
      id="cam-select-portal"
      style={{
        position: 'fixed',
        top:   pos.top,
        left:  pos.left,
        width: Math.max(pos.width, 280),
        background: '#0a0a0a',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '5px',
        zIndex: 9999,
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
      }}
    >
      {/* Search */}
      <div style={{ padding:'10px 12px', borderBottom:'1px solid rgba(255,255,255,0.08)', display:'flex', alignItems:'center', gap:'8px', background:'#0d0d0d' }}>
        <span style={{ fontSize:'14px', color:'#555555' }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search cameras…"
          style={{ flex:1, background:'none', border:'none', outline:'none', color:'white', fontSize:'13px', fontFamily:'inherit' }}
        />
        {query && (
          <button onClick={() => setQuery('')} style={{ background:'none', border:'none', color:'#555555', fontSize:'13px', cursor:'pointer', padding:0, lineHeight:1 }}>✕</button>
        )}
      </div>

      {/* List */}
      <div style={{ maxHeight:'320px', overflowY:'auto', overflowX:'hidden' }}>
        {/* Empty slot */}
        <div
          onClick={() => select(null)}
          style={{ padding:'10px 14px', cursor:'pointer', fontSize:'12px', color:'#555555', borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center', gap:'8px' }}
          onMouseEnter={e => (e.currentTarget.style.background='rgba(255,255,255,0.04)')}
          onMouseLeave={e => (e.currentTarget.style.background='transparent')}
        >
          <span style={{ fontSize:'16px' }}>✕</span> Empty slot
        </div>

        {filtered.length === 0 && (
          <div style={{ padding:'20px', fontSize:'12px', color:'#444444', textAlign:'center' }}>
            No cameras match "{query}"
          </div>
        )}

        {filtered.length === 0 && cameras.length === 0 && !loading && (
          <div style={{ padding:'20px', fontSize:'12px', color:'#444444', textAlign:'center', lineHeight:1.6 }}>
            No cameras found.<br/>
            <span style={{ fontSize:'11px' }}>Connect a CCTV integration first.</span>
          </div>
        )}

        {Object.entries(filteredByInteg).map(([integLabel, cams]) => (
          <div key={integLabel}>
            {/* Group header */}
            <div style={{ padding:'8px 14px 5px', fontSize:'10px', color:'#3b82f6', fontWeight:700, letterSpacing:'0.06em', background:'rgba(59,130,246,0.05)', borderTop:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center', gap:'6px' }}>
              <span>📹</span> {integLabel}
            </div>
            {cams.map(cam => {
              const isSelected = value === cam.id;
              return (
                <div
                  key={cam.id}
                  onClick={() => select(cam.id)}
                  style={{
                    padding: '10px 14px 10px 24px',
                    cursor: 'pointer',
                    background: isSelected ? 'rgba(59,130,246,0.12)' : 'transparent',
                    borderLeft: `3px solid ${isSelected ? '#3b82f6' : 'transparent'}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                  onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background='rgba(255,255,255,0.05)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isSelected?'rgba(59,130,246,0.12)':'transparent'; }}
                >
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'13px', color: isSelected?'#93c5fd':'white', fontWeight: isSelected?700:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {cam.name}
                    </div>
                    {cam.channel !== undefined && (
                      <div style={{ fontSize:'10px', color:'#555555', marginTop:'2px' }}>
                        CH{cam.channel} · {cam.type.toUpperCase()}
                      </div>
                    )}
                  </div>
                  {isSelected && <span style={{ fontSize:'12px', color:'#3b82f6', flexShrink:0 }}>✓</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        onClick={openDropdown}
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: `1px solid ${open ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.1)'}`,
          borderRadius: '4px',
          padding: '9px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'border-color 0.15s',
          userSelect: 'none',
        }}
      >
        {selected ? (
          <>
            <div style={{ fontSize:'9px', color:'#3b82f6', background:'rgba(59,130,246,0.15)', padding:'2px 6px', borderRadius:'4px', fontWeight:700, flexShrink:0 }}>
              {selected.type.toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'12px', color:'white', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected.name}</div>
              <div style={{ fontSize:'10px', color:'#555555', marginTop:'1px' }}>{selected.integrationLabel}</div>
            </div>
          </>
        ) : (
          <div style={{ flex:1, fontSize:'12px', color:'#555555' }}>
            {loading ? '⏳ Loading cameras…' : cameras.length === 0 ? 'No cameras available' : '— Select camera —'}
          </div>
        )}
        <span style={{ fontSize:'10px', color:'#555555', flexShrink:0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {dropdown}
    </>
  );
}

// ─── Edit / Create View Modal ─────────────────────────────────────────────────

function EditViewModal({
  cameras, loading, existing, onSave, onClose,
}: {
  cameras: DiscoveredCamera[];
  loading: boolean;
  existing?: CameraView;
  onSave: (v: CameraView) => void;
  onClose: () => void;
}) {
  const [name, setName]     = useState(existing?.name || '');
  const [layout, setLayout] = useState<LayoutType>(existing?.layout || '2x2');
  const [cells, setCells]   = useState<ViewCell[]>(existing ? existing.cells : makeEmptyCells('2x2'));

  function changeLayout(l: LayoutType) {
    const total = LAYOUT_META[l].total;
    setCells(prev => {
      const next = makeEmptyCells(l);
      for (let i = 0; i < Math.min(prev.length, total); i++) next[i].cameraId = prev[i]?.cameraId || null;
      return next;
    });
    setLayout(l);
  }

  function assignCamera(slot: number, cameraId: string | null) {
    setCells(prev => prev.map(c => c.slot === slot ? { ...c, cameraId } : c));
  }

  function save() {
    if (!name.trim()) return;
    onSave({
      id: existing?.id || `view-${Date.now()}`,
      name: name.trim(),
      layout,
      cells,
      createdAt: existing?.createdAt || new Date().toISOString(),
    });
    onClose();
  }

  const meta = LAYOUT_META[layout];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.82)', zIndex:300, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'18px', width:'100%', maxWidth:'680px' }}>
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:'16px', color:'white' }}>{existing ? 'Edit View' : 'Create New View'}</div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'4px', color:'#555555', width:'30px', height:'30px', fontSize:'15px', cursor:'pointer' }}>✕</button>
        </div>

        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:'18px' }}>
          {/* Name */}
          <div>
            <label style={{ fontSize:'10px', color:'#555555', display:'block', marginBottom:'6px', fontWeight:700, letterSpacing:'0.05em' }}>VIEW NAME *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Ground Floor, Carpark Cameras, Home"
              style={{ width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'9px 12px', color:'white', fontSize:'13px' }} />
          </div>

          {/* Layout picker */}
          <div>
            <label style={{ fontSize:'10px', color:'#555555', display:'block', marginBottom:'8px', fontWeight:700, letterSpacing:'0.05em' }}>LAYOUT</label>
            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
              {(Object.keys(LAYOUT_META) as LayoutType[]).map(l => {
                const m = LAYOUT_META[l];
                return (
                  <button key={l} onClick={() => changeLayout(l)}
                    style={{ padding:'10px 16px', borderRadius:'4px', border:`1px solid ${layout===l?'rgba(59,130,246,0.5)':'rgba(255,255,255,0.08)'}`, background:layout===l?'rgba(59,130,246,0.15)':'rgba(255,255,255,0.03)', color:layout===l?'#93c5fd':'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'3px' }}>
                    <span style={{ fontSize:'16px' }}>{m.icon}</span>
                    <span>{m.label}</span>
                    <span style={{ fontSize:'10px', opacity:0.6 }}>{m.total} cam{m.total>1?'s':''}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cell assignment */}
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px' }}>
              <label style={{ fontSize:'10px', color:'#555555', fontWeight:700, letterSpacing:'0.05em' }}>ASSIGN CAMERAS TO CELLS</label>
              {loading && <span style={{ fontSize:'10px', color:'#555555' }}>⏳ Loading cameras…</span>}
              {!loading && cameras.length > 0 && <span style={{ fontSize:'10px', color:'#22c55e' }}>✓ {cameras.length} camera{cameras.length>1?'s':''} available</span>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(meta.cols, 3)},1fr)`, gap:'10px' }}>
              {cells.map(cell => {
                const assigned = cell.cameraId ? cameras.find(c => c.id === cell.cameraId) : null;
                return (
                  <div key={cell.slot} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', overflow:'hidden' }}>
                    {/* Mini preview */}
                    <div style={{ aspectRatio:'16/9', background: assigned ? 'rgba(59,130,246,0.08)' : '#050810', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
                      {assigned ? (
                        <div style={{ textAlign:'center', padding:'8px' }}>
                          <div style={{ fontSize:'16px', marginBottom:'4px' }}>📹</div>
                          <div style={{ fontSize:'9px', color:'#93c5fd', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%' }}>{assigned.name}</div>
                        </div>
                      ) : (
                        <div style={{ fontSize:'20px', opacity:0.1 }}>📷</div>
                      )}
                      <div style={{ position:'absolute', top:'4px', left:'4px', fontSize:'8px', color:'#444444', fontWeight:700 }}>CELL {cell.slot + 1}</div>
                    </div>
                    <div style={{ padding:'8px' }}>
                      <CameraSelect cameras={cameras} value={cell.cameraId} onChange={id => assignCamera(cell.slot, id)} loading={loading} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
            <button onClick={onClose} style={{ padding:'10px 22px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'13px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={save} disabled={!name.trim()}
              style={{ padding:'10px 28px', borderRadius:'4px', border:'none', background: name.trim()?'linear-gradient(135deg,#3b82f6,#1d4ed8)':'rgba(255,255,255,0.05)', color: name.trim()?'white':'#555555', fontSize:'13px', fontWeight:700, cursor: name.trim()?'pointer':'default' }}>
              {existing ? 'Save Changes' : 'Create View'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Live Grid ────────────────────────────────────────────────────────────────

function LiveGrid({ view, cameras, onEditView }: {
  view: CameraView;
  cameras: DiscoveredCamera[];
  onEditView: () => void;
}) {
  const [fullscreenCam, setFullscreenCam] = useState<DiscoveredCamera | null>(null);
  const meta = LAYOUT_META[view.layout];
  const camById = Object.fromEntries(cameras.map(c => [c.id, c]));

  const is1plus5 = view.layout === '1+5';
  const is2plus4 = view.layout === '2+4';

  const EditBtn = () => (
    <div style={{ textAlign:'center', marginTop:'10px' }}>
      <button onClick={onEditView} style={{ background:'#111111', border:'1px solid #1a1a1a', borderRadius:'4px', color:'#555555', fontSize:'11px', padding:'5px 16px', cursor:'pointer' }}>
        ✏️ Edit View
      </button>
    </div>
  );

  if (is1plus5 || is2plus4) {
    const mainCount = is2plus4 ? 2 : 1;
    const mainCells = view.cells.slice(0, mainCount);
    const smallCells = view.cells.slice(mainCount);
    return (
      <>
        {fullscreenCam && <FullscreenViewer camera={fullscreenCam} onClose={() => setFullscreenCam(null)} />}
        <div style={{ display:'grid', gridTemplateColumns: '2fr 1fr', gap:0, border:'1px solid #222' }}>
          <div style={{ display:'grid', gridTemplateRows:`repeat(${mainCount},1fr)`, gap:0 }}>
            {mainCells.map(cell => {
              const cam = cell.cameraId ? camById[cell.cameraId] || null : null;
              return <div key={cell.slot} style={{ border:'1px solid #222' }}><SnapshotCell camera={cam} onFullscreen={cam ? () => setFullscreenCam(cam) : undefined} /></div>;
            })}
          </div>
          <div style={{ display:'grid', gridTemplateRows:`repeat(${smallCells.length},1fr)`, gap:0, borderLeft:'1px solid #222' }}>
            {smallCells.map(cell => {
              const cam = cell.cameraId ? camById[cell.cameraId] || null : null;
              return <div key={cell.slot} style={{ border:'1px solid #222' }}><SnapshotCell camera={cam} onFullscreen={cam ? () => setFullscreenCam(cam) : undefined} /></div>;
            })}
          </div>
        </div>
        <EditBtn />
      </>
    );
  }

  return (
    <>
      {fullscreenCam && <FullscreenViewer camera={fullscreenCam} onClose={() => setFullscreenCam(null)} />}
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${meta.cols},1fr)`, gap:0, border:'1px solid #222' }}>
        {view.cells.map(cell => {
          const cam = cell.cameraId ? camById[cell.cameraId] || null : null;
          return (
            <div key={cell.slot} style={{ border:'1px solid #222' }}>
              <SnapshotCell camera={cam} onFullscreen={cam ? () => setFullscreenCam(cam) : undefined} />
            </div>
          );
        })}
      </div>
      <EditBtn />
    </>
  );
}

// ─── Native HLS / Direct stream player ───────────────────────────────────────

function StreamPlayer({ url, label, onClose }: { url: string; label: string; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.src = url;
    v.play().catch(e => setError(`Playback error: ${e.message || 'Check CORS/stream URL'}`));
  }, [url]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.95)', zIndex:500, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <div style={{ position:'relative', width:'min(900px,96vw)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', marginBottom:'8px' }}>
          <div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'white' }}>{label}</div>
            <div style={{ fontSize:'11px', color:'#555555', fontFamily:'monospace', marginTop:'2px' }}>{url}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:'4px', color:'white', padding:'6px 14px', cursor:'pointer', fontSize:'12px', fontWeight:600 }}>✕ Close</button>
        </div>
        {error ? (
          <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'4px', padding:'24px', textAlign:'center' }}>
            <div style={{ fontSize:'14px', color:'#ef4444', fontWeight:600, marginBottom:'8px' }}>Stream Error</div>
            <div style={{ fontSize:'12px', color:'#a0a0a0' }}>{error}</div>
            <div style={{ fontSize:'11px', color:'#555555', marginTop:'8px' }}>For RTSP streams, configure a proxy (MediaMTX, nginx-rtmp) to serve HLS/WebRTC</div>
          </div>
        ) : (
          <video
            ref={videoRef}
            controls
            autoPlay
            muted
            playsInline
            style={{ width:'100%', borderRadius:'4px', background:'#000', maxHeight:'70vh' }}
          />
        )}
        <div style={{ marginTop:'8px', fontSize:'10px', color:'#444444', textAlign:'center' }}>
          Native HLS plays in Safari/iOS natively. Chrome/Firefox requires an HLS-to-WebRTC proxy or MediaMTX.
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Video() {
  const { toast } = useToast();
  const [views, setViews]                   = useLocalStorage<CameraView[]>('lemai_views', []);
  const [activeViewId, setActiveViewId]     = useLocalStorage<string>('lemai_active_view', '');
  const { cameras, loading, refresh }       = useIntegrationCameras();

  const [tab, setTab]                       = useState<'live' | 'views' | 'map'>('live');
  const [streamUrl, setStreamUrl]           = useState<{ url: string; label: string } | null>(null);
  const [gridLayout, setGridLayout]         = useState<'1x1'|'2x2'|'3x3'|'4x4'>('2x2');
  const [viewMode, setViewMode]             = useState<'live'|'playback'>('live');
  const [selectedTile, setSelectedTile]     = useState<number|null>(null);
  const [pbSpeed, setPbSpeed]               = useState<number>(1);
  const [camSearch, setCamSearch]           = useState('');
  const [selectedCamId, setSelectedCamId]  = useState<string|null>(null);
  const [isPtz, setIsPtz]                  = useState(false);
  const { editing, setEditing, slots, isVisible, orderedWidgets, toggle, moveUp, moveDown, reset } = usePageCustomizer('video', VIDEO_WIDGETS);
  const [showCreateView, setShowCreateView] = useState(false);
  const [editingView, setEditingView]       = useState<CameraView | undefined>();

  const activeView = views.find(v => v.id === activeViewId) || views[0] || null;

  const saveView = useCallback((v: CameraView) => {
    setViews(prev => {
      const idx = prev.findIndex(x => x.id === v.id);
      const isEdit = idx >= 0;
      toast(isEdit ? `View updated: ${v.name}` : `View created: ${v.name}`, 'success');
      return isEdit ? prev.map(x => x.id === v.id ? v : x) : [...prev, v];
    });
    setActiveViewId(v.id);
  }, [setViews, setActiveViewId, toast]);

  const deleteView = useCallback((id: string) => {
    setViews(prev => {
      const view = prev.find(v => v.id === id);
      if (view) toast(`View deleted: ${view.name}`, 'warning');
      return prev.filter(v => v.id !== id);
    });
    if (activeViewId === id) {
      const remaining = views.filter(v => v.id !== id);
      setActiveViewId(remaining.length > 0 ? remaining[0].id : '');
    }
  }, [setViews, activeViewId, views, setActiveViewId, toast]);

  const tabStyle = (t: string): React.CSSProperties => ({
    height:'32px', padding:'0 14px', border:'none',
    borderBottom:`2px solid ${tab===t?'#3b82f6':'transparent'}`,
    background:'transparent', cursor:'pointer',
    fontSize:'10px', fontWeight:700, letterSpacing:'0.06em',
    color:tab===t?'#e5e5e5':'#444',
    transition:'color 0.12s',
  });
  const tbBtn = (active: boolean): React.CSSProperties => ({
    width:'28px', height:'28px', border:`1px solid ${active?'#3b82f6':'#333'}`,
    background:active?'#1e3a5f':'#1a1a1a', color:active?'#3b82f6':'#888',
    borderRadius:0, cursor:'pointer', fontSize:'9px', fontWeight:700,
    display:'flex', alignItems:'center', justifyContent:'center',
  });

  // Group cameras by integration label for tree display
  const camsByGroup: Record<string, typeof cameras> = {};
  cameras
    .filter(c => !camSearch || c.name.toLowerCase().includes(camSearch.toLowerCase()) || c.integrationLabel.toLowerCase().includes(camSearch.toLowerCase()))
    .forEach(c => {
      const g = c.integrationLabel || 'Other';
      if (!camsByGroup[g]) camsByGroup[g] = [];
      camsByGroup[g].push(c);
    });

  return (
    <Layout title="Video" subtitle="Live camera feeds and custom views">
      <style>{`
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes alarmPulse{0%,100%{opacity:1}50%{opacity:0.4}}
      `}</style>
      {streamUrl && <StreamPlayer url={streamUrl.url} label={streamUrl.label} onClose={() => setStreamUrl(null)} />}
      {editing && <CustomizerPanel widgets={VIDEO_WIDGETS} slots={slots} toggle={toggle} moveUp={moveUp} moveDown={moveDown} reset={reset} onClose={() => setEditing(false)} />}

      {showCreateView && (
        <EditViewModal cameras={cameras} loading={loading} onSave={saveView} onClose={() => setShowCreateView(false)} />
      )}
      {editingView && (
        <EditViewModal cameras={cameras} loading={loading} existing={editingView} onSave={saveView} onClose={() => setEditingView(undefined)} />
      )}

      <div style={{ display:'flex', height:'calc(100vh - 120px)', minHeight:'500px' }}>

      {/* ── LEFT CAMERA TREE (200px) ── */}
      <div style={{ width:'200px', flexShrink:0, background:'#111', borderRight:'1px solid #222', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ padding:'8px', borderBottom:'1px solid #222', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:'9px', fontWeight:700, color:'#555', textTransform:'uppercase', letterSpacing:'0.1em' }}>CAMERAS</span>
          <button onClick={()=>setShowCreateView(true)} style={{ background:'none', border:'none', color:'#3b82f6', cursor:'pointer', fontSize:'10px', fontWeight:700, padding:0 }}>+ View</button>
        </div>
        {/* Search */}
        <div style={{ padding:'4px 8px', borderBottom:'1px solid #222' }}>
          <input
            value={camSearch}
            onChange={e=>setCamSearch(e.target.value)}
            placeholder="Search cameras..."
            style={{ width:'100%', height:'24px', background:'#0a0a0a', border:'1px solid #222', borderRadius:0, padding:'0 6px', color:'#a0a0a0', fontSize:'9px', outline:'none', boxSizing:'border-box' }}
          />
        </div>
        {/* Views section */}
        {views.length > 0 && (
          <div style={{ borderBottom:'1px solid #1a1a1a' }}>
            <div style={{ height:'22px', padding:'0 8px', display:'flex', alignItems:'center', fontSize:'9px', fontWeight:700, color:'#555', textTransform:'uppercase', letterSpacing:'0.08em', borderBottom:'1px solid #1a1a1a' }}>
              SAVED VIEWS
            </div>
            {views.map(v => (
              <div key={v.id}
                onClick={()=>{ setActiveViewId(v.id); setTab('live'); }}
                style={{ height:'22px', padding:'0 8px 0 16px', display:'flex', alignItems:'center', cursor:'pointer', background:activeViewId===v.id&&tab==='live'?'#1e3a5f':'transparent' }}
                onMouseEnter={e=>{ if(!(activeViewId===v.id&&tab==='live'))(e.currentTarget as HTMLElement).style.background='#1a1a1a'; }}
                onMouseLeave={e=>{ (e.currentTarget as HTMLElement).style.background=activeViewId===v.id&&tab==='live'?'#1e3a5f':'transparent'; }}
              >
                <span style={{ fontSize:'10px', color:activeViewId===v.id&&tab==='live'?'#e5e5e5':'#a0a0a0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{LAYOUT_META[v.layout]?.icon} {v.name}</span>
              </div>
            ))}
          </div>
        )}
        {/* Camera tree */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {loading ? (
            <div style={{ padding:'12px 8px', fontSize:'9px', color:'#444' }}>Discovering cameras…</div>
          ) : cameras.length === 0 ? (
            <div style={{ padding:'12px 8px', fontSize:'9px', color:'#444', textAlign:'center' }}>No cameras found.<br/>Connect an integration.</div>
          ) : (
            Object.entries(camsByGroup).map(([group, cams]) => (
              <div key={group}>
                {/* Site group header */}
                <div style={{ height:'22px', padding:'0 8px', display:'flex', alignItems:'center', fontSize:'9px', fontWeight:700, color:'#555', textTransform:'uppercase', letterSpacing:'0.08em', borderBottom:'1px solid #1a1a1a', background:'#0d0d0d' }}>
                  {group}
                </div>
                {cams.map(cam => {
                  const dotColor = cam.type === 'offline' ? '#dc2626' : cam.snapshotUrl ? '#16a34a' : '#3b82f6';
                  const isActive = selectedCamId === cam.id;
                  return (
                    <div key={cam.id}
                      onClick={()=>{ setSelectedCamId(cam.id); setIsPtz(false); }}
                      style={{ height:'22px', padding:'0 8px 0 16px', display:'flex', alignItems:'center', gap:'5px', cursor:'pointer', background:isActive?'#1e3a5f':'transparent' }}
                      onMouseEnter={e=>{ if(!isActive)(e.currentTarget as HTMLElement).style.background='#1a1a1a'; }}
                      onMouseLeave={e=>{ (e.currentTarget as HTMLElement).style.background=isActive?'#1e3a5f':'transparent'; }}
                    >
                      <div style={{ width:6, height:6, borderRadius:'50%', background:dotColor, flexShrink:0 }} />
                      <span style={{ fontSize:'10px', color:isActive?'#e5e5e5':'#a0a0a0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{cam.name}</span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── CENTER + RIGHT ── */}
      <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* ── TOP TOOLBAR (32px) ── */}
        <div style={{ height:'32px', background:'#0f0f0f', borderBottom:'1px solid #222', display:'flex', alignItems:'center', gap:'4px', padding:'0 8px', flexShrink:0 }}>
          {/* Layout selector */}
          {(['1x1','2x2','3x3','4x4'] as const).map(l => (
            <button key={l} style={tbBtn(gridLayout===l)} onClick={()=>setGridLayout(l)} title={`${l} grid`}>
              {l}
            </button>
          ))}
          <div style={{ width:'1px', background:'#222', height:'18px', margin:'0 4px' }} />
          {/* LIVE / PLAYBACK */}
          <button style={tbBtn(viewMode==='live')} onClick={()=>setViewMode('live')}>LIVE</button>
          <button style={tbBtn(viewMode==='playback')} onClick={()=>setViewMode('playback')}>PLAY</button>
          {/* Playback controls (playback mode only) */}
          {viewMode === 'playback' && (<>
            <div style={{ width:'1px', background:'#222', height:'18px', margin:'0 4px' }} />
            {(['|◀','◀◀','▶','▶▶','▶|'] as const).map(lbl => (
              <button key={lbl} style={{ width:'24px', height:'24px', border:'1px solid #333', background:'#1a1a1a', color:'#e5e5e5', borderRadius:0, cursor:'pointer', fontSize:'9px', display:'flex', alignItems:'center', justifyContent:'center' }}>{lbl}</button>
            ))}
            <div style={{ width:'1px', background:'#222', height:'18px', margin:'0 4px' }} />
            {([0.5,1,2,4] as const).map(s => (
              <button key={s} style={tbBtn(pbSpeed===s)} onClick={()=>setPbSpeed(s)}>{s}×</button>
            ))}
          </>)}
          <div style={{ flex:1 }} />
          {/* Refresh + customize */}
          <button onClick={refresh} style={{ width:'24px', height:'24px', border:'1px solid #333', background:'#1a1a1a', color:'#888', borderRadius:0, cursor:'pointer', fontSize:'11px' }}>↻</button>
          <CustomizeButton editing={editing} setEditing={setEditing} />
        </div>

        {/* Tab strip */}
        <div style={{ borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center', background:'#0d0d0d', flexShrink:0 }}>
          {orderedWidgets.filter(w => isVisible(w.id)).map(w => {
            const t = w.id as 'live'|'map'|'views';
            const labels: Record<string,string> = { live:'LIVE VIEW', map:'CAMERA MAP', views:`MANAGE VIEWS (${views.length})` };
            return <button key={t} style={tabStyle(t)} onClick={() => setTab(t)}>{labels[t]||t}</button>;
          })}
        </div>

        {/* ── MAIN CANVAS + TIMELINE wrapper ── */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#0a0a0a' }}>
        <div style={{ flex:1, overflow:'auto' }}>

        {/* ── Live View ── */}
        {tab === 'live' && (
          <div style={{ padding:'8px' }}>
            {views.length === 0 ? (
              loading ? (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'8px', padding:'16px' }}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="skeleton" style={{ aspectRatio:'16/9', borderRadius:'4px' }} />
                  ))}
                </div>
              ) : cameras.length === 0 ? (
                /* Step-by-step guide when no cameras and no views */
                <div style={{ padding:'40px 20px', maxWidth:'520px', margin:'0 auto' }}>
                  <div style={{ textAlign:'center', marginBottom:'32px' }}>
                    <div style={{ fontSize:'48px', marginBottom:'10px', opacity:0.5 }}>📡</div>
                    <div style={{ fontSize:'16px', fontWeight:700, color:'white', marginBottom:'6px' }}>No cameras connected</div>
                    <div style={{ fontSize:'12px', color:'#555555' }}>Follow the steps below to set up live video monitoring</div>
                  </div>
                  {[
                    { step:'1', icon:'🔌', title:'Connect a CCTV Integration', desc:'Go to Settings → Integrations and add Dahua NVR, Milestone XProtect, or an RTSP source.', action:{ label:'Open Integrations →', href:'/m/integrations' } },
                    { step:'2', icon:'📋', title:'Create a Camera View', desc:'Create a named layout (2×2, 3×3, etc.) and assign cameras to each cell.', action:{ label:'+ Create View', onClick:() => setShowCreateView(true) } },
                    { step:'3', icon:'📺', title:'Monitor Live Feeds', desc:'Snapshots refresh automatically. Use the Site Map tab to see camera positions.', action:null },
                  ].map(s => (
                    <div key={s.step} style={{ display:'flex', gap:'14px', padding:'14px', borderRadius:'4px', background:'#0d0d0d', border:'1px solid #1a1a1a', marginBottom:'10px' }}>
                      <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:700, color:'#3b82f6', flexShrink:0 }}>{s.step}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5', marginBottom:'3px' }}>{s.icon} {s.title}</div>
                        <div style={{ fontSize:'11px', color:'#555555', marginBottom: s.action ? '8px' : 0 }}>{s.desc}</div>
                        {s.action && s.action.href && <a href={s.action.href} style={{ fontSize:'11px', color:'#3b82f6', textDecoration:'none', fontWeight:600 }}>{s.action.label}</a>}
                        {s.action && s.action.onClick && <button onClick={s.action.onClick} style={{ fontSize:'11px', color:'#3b82f6', background:'none', border:'none', cursor:'pointer', fontWeight:600, padding:0 }}>{s.action.label}</button>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Has cameras but no views yet */
                <div style={{ textAlign:'center', padding:'60px 20px' }}>
                  <div style={{ fontSize:'52px', marginBottom:'14px', opacity:0.6 }}>📺</div>
                  <div style={{ fontSize:'15px', fontWeight:700, color:'white', marginBottom:'6px' }}>No views configured</div>
                  <div style={{ fontSize:'13px', color:'#555555', marginBottom:'6px' }}>
                    {cameras.length} camera{cameras.length>1?'s':''} discovered. Create a named view, choose a layout, and assign cameras to each cell.
                  </div>
                  <button onClick={() => setShowCreateView(true)}
                    style={{ padding:'11px 28px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer', boxShadow:'0 4px 14px rgba(59,130,246,0.4)' }}>
                    + Create First View
                  </button>
                </div>
              )
            ) : (
              <>
                {/* View selector */}
                {views.length > 1 && (
                  <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center' }}>
                    {views.map(v => (
                      <button key={v.id} onClick={() => setActiveViewId(v.id)}
                        style={{ padding:'3px 10px', border:`1px solid ${activeViewId===v.id?'#3b82f6':'#333'}`, background:activeViewId===v.id?'#1e3a5f':'#1a1a1a', color:activeViewId===v.id?'#3b82f6':'#555', fontSize:'10px', fontWeight:700, cursor:'pointer', borderRadius:0 }}>
                        {LAYOUT_META[v.layout].icon} {v.name}
                      </button>
                    ))}
                  </div>
                )}

                {activeView && loading && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'8px', marginBottom:'10px' }}>
                    {Array.from({ length: activeView.cells.length || 4 }).map((_, i) => (
                      <div key={i} className="skeleton" style={{ aspectRatio:'16/9', borderRadius:'4px' }} />
                    ))}
                  </div>
                )}
                {activeView && !loading && (
                  <LiveGrid view={activeView} cameras={cameras} onEditView={() => setEditingView(activeView)} />
                )}

                {cameras.length === 0 && !loading && (
                  <div style={{ marginTop:'14px', padding:'12px 16px', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'4px', fontSize:'12px', color:'#555555' }}>
                    💡 <strong style={{ color:'#93c5fd' }}>Getting started:</strong> Go to <strong style={{ color:'#93c5fd' }}>Integrations</strong> and add a CCTV integration (Dahua, Milestone, ONVIF, etc). Once connected, cameras will appear automatically here.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Camera Map ── */}
        {tab === 'map' && (() => {
          // Build map cameras: use cameras assigned to the active view first,
          // then fill remaining slots with any discovered cameras not yet placed.
          const viewCameraIds: string[] = activeView
            ? activeView.cells.map(c => c.cameraId).filter(Boolean) as string[]
            : [];

          // Assigned cameras in this view, placed on map using DEFAULT_CAMERAS positions
          const viewMapCams = viewCameraIds
            .map((id, i) => {
              const discovered = cameras.find(c => c.id === id);
              const pos = DEFAULT_CAMERAS[i % DEFAULT_CAMERAS.length];
              return {
                id,
                name: discovered?.name || id,
                zone: pos.zone,
                status: discovered?.snapshotUrl ? 'online' as const : 'offline' as const,
                cx: pos.cx,
                cy: pos.cy,
              };
            });

          // Cameras discovered but not in view — shown dimmed at remaining positions
          const extraCams = cameras
            .filter(c => !viewCameraIds.includes(c.id))
            .slice(0, 10 - viewMapCams.length)
            .map((c, i) => {
              const pos = DEFAULT_CAMERAS[(viewMapCams.length + i) % DEFAULT_CAMERAS.length];
              return { id: c.id, name: c.name, zone: pos.zone, status: 'offline' as const, cx: pos.cx, cy: pos.cy };
            });

          const mapCams = viewMapCams.length > 0 ? viewMapCams : (cameras.length > 0 ? extraCams : DEFAULT_CAMERAS);

          return (
            <div style={{ padding:'16px' }}>
              {/* View selector */}
              {views.length > 0 && (
                <div style={{ display:'flex', gap:'6px', marginBottom:'12px', alignItems:'center', flexWrap:'wrap' }}>
                  <span style={{ fontSize:'10px', color:'#555555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Showing view:</span>
                  {views.map(v => (
                    <button key={v.id} onClick={() => setActiveViewId(v.id)}
                      style={{ padding:'2px 8px', border:`1px solid ${activeViewId===v.id?'#3b82f6':'#333'}`, background:activeViewId===v.id?'#1e3a5f':'#1a1a1a', color:activeViewId===v.id?'#3b82f6':'#555', fontSize:'10px', fontWeight:700, cursor:'pointer', borderRadius:0 }}>
                      {LAYOUT_META[v.layout].icon} {v.name}
                      <span style={{ marginLeft:'5px', fontSize:'9px', opacity:0.6 }}>
                        ({v.cells.filter(c=>c.cameraId).length} cams)
                      </span>
                    </button>
                  ))}
                  <span style={{ fontSize:'10px', color:'#444444', marginLeft:'4px' }}>
                    · Click a camera on the map to add it to this view
                  </span>
                </div>
              )}
              {views.length === 0 && (
                <div style={{ fontSize:'11px', color:'#a0a0a0', marginBottom:'12px', background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'4px', padding:'8px 12px' }}>
                  <span style={{ color:'#3b82f6', fontWeight:600 }}>Camera Layout Map</span>
                  {' '}— Create a view first, then click cameras to add them.
                </div>
              )}
              <SiteMap
                mode="video"
                cameras={mapCams}
                onCameraClick={(cam) => {
                  if (activeView) {
                    const emptySlot = activeView.cells.find(c => !c.cameraId);
                    if (emptySlot) {
                      const updated: CameraView = {
                        ...activeView,
                        cells: activeView.cells.map(c => c.slot === emptySlot.slot ? { ...c, cameraId: cam.id } : c),
                      };
                      saveView(updated);
                    }
                  }
                  setTab('live');
                }}
                height={460}
              />
            </div>
          );
        })()}

        {/* ── Manage Views ── */}
        {tab === 'views' && (
          <div style={{ padding:'20px' }}>
            {views.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 20px', color:'#555555', fontSize:'13px' }}>No views yet — create one.</div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:'12px' }}>
                {views.map(v => {
                  const meta = LAYOUT_META[v.layout];
                  const assigned = v.cells.filter(c => c.cameraId).length;
                  const isActive = v.id === (activeView?.id);
                  return (
                    <div key={v.id} style={{ background: isActive?'rgba(59,130,246,0.06)':'rgba(255,255,255,0.02)', border:`1px solid ${isActive?'rgba(59,130,246,0.3)':'rgba(255,255,255,0.07)'}`, borderRadius:'5px', padding:'16px' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:'10px', marginBottom:'12px' }}>
                        <div style={{ fontSize:'26px' }}>{meta.icon}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:'14px', fontWeight:700, color:'white' }}>{v.name}</div>
                          <div style={{ fontSize:'11px', color:'#555555', marginTop:'2px' }}>{meta.label} · {assigned}/{meta.total} cameras assigned</div>
                          {isActive && <div style={{ fontSize:'9px', color:'#3b82f6', fontWeight:700, marginTop:'3px' }}>● ACTIVE</div>}
                        </div>
                      </div>

                      {/* Mini grid preview */}
                      <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(meta.cols,4)},1fr)`, gap:'3px', marginBottom:'12px' }}>
                        {v.cells.slice(0, Math.min(meta.total, 8)).map(cell => {
                          const cam = cell.cameraId ? cameras.find(c => c.id === cell.cameraId) : null;
                          return (
                            <div key={cell.slot} style={{ aspectRatio:'16/9', borderRadius:'3px', background: cam?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.04)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                              {cam && <div style={{ fontSize:'6px', color:'#93c5fd', padding:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%', textAlign:'center' }}>{cam.name}</div>}
                            </div>
                          );
                        })}
                        {meta.total > 8 && <div style={{ aspectRatio:'16/9', borderRadius:'3px', background:'#0d0d0d', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'8px', color:'#444444' }}>+{meta.total-8}</div>}
                      </div>

                      <div style={{ display:'flex', gap:'6px' }}>
                        <button onClick={() => { setActiveViewId(v.id); setTab('live'); }}
                          style={{ flex:1, padding:'7px', borderRadius:'7px', border:'none', background: isActive?'rgba(59,130,246,0.2)':'rgba(59,130,246,0.1)', color:'#93c5fd', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
                          {isActive ? '▶ Watching' : '▶ Watch'}
                        </button>
                        <button onClick={() => setEditingView(v)}
                          style={{ flex:1, padding:'7px', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'#111111', color:'#a0a0a0', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                          ✏️ Edit
                        </button>
                        <button onClick={() => { if (window.confirm(`Delete "${v.name}"?`)) deleteView(v.id); }}
                          style={{ padding:'7px 10px', borderRadius:'7px', border:'1px solid rgba(239,68,68,0.2)', background:'rgba(239,68,68,0.06)', color:'#f87171', fontSize:'11px', cursor:'pointer' }}>
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

        {/* ── TIMELINE (24px) ── */}
        {tab === 'live' && (
          <div style={{ height:'24px', background:'#111', borderTop:'1px solid #222', flexShrink:0, position:'relative', overflow:'hidden' }}>
            {/* Recorded segments */}
            <div style={{ position:'absolute', top:'4px', left:'20%', width:'40%', height:'12px', background:'rgba(59,130,246,0.4)' }} />
            <div style={{ position:'absolute', top:'4px', left:'65%', width:'20%', height:'12px', background:'rgba(59,130,246,0.4)' }} />
            {/* Motion markers */}
            <div style={{ position:'absolute', bottom:0, left:'22%', width:'2px', height:'2px', background:'#f59e0b' }} />
            <div style={{ position:'absolute', bottom:0, left:'35%', width:'2px', height:'2px', background:'#f59e0b' }} />
            <div style={{ position:'absolute', bottom:0, left:'50%', width:'2px', height:'2px', background:'#f59e0b' }} />
            {/* Alarm markers */}
            <div style={{ position:'absolute', bottom:0, left:'42%', width:'2px', height:'2px', background:'#dc2626' }} />
            {/* Playhead */}
            <div style={{ position:'absolute', top:0, bottom:0, left:'50%', width:'1px', background:'#dc2626' }} />
            {/* Time labels */}
            {[0,25,50,75,100].map(pct => (
              <div key={pct} style={{ position:'absolute', top:'2px', left:`${pct}%`, fontSize:'8px', fontFamily:'monospace', color:'#555', transform:'translateX(-50%)' }}>
                {String(Math.floor(pct/100*24)).padStart(2,'0')}:00
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* ── PTZ CONTROLS (80px, shown when PTZ camera selected) ── */}
      {isPtz && selectedCamId && (
        <div style={{ width:'80px', flexShrink:0, background:'#111', borderLeft:'1px solid #222', display:'flex', flexDirection:'column', alignItems:'center', padding:'8px 4px', gap:'4px' }}>
          <div style={{ fontSize:'8px', color:'#444', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'4px' }}>PTZ</div>
          {/* Direction pad */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'2px' }}>
            {['↖','↑','↗','←','·','→','↙','↓','↘'].map((d,i) => (
              <button key={i} style={{ width:'24px', height:'24px', border:'1px solid #333', background:'#1a1a1a', color: d==='·'?'#333':'#888', borderRadius:0, cursor:d==='·'?'default':'pointer', fontSize:'10px', display:'flex', alignItems:'center', justifyContent:'center' }}>{d}</button>
            ))}
          </div>
          <div style={{ fontSize:'8px', color:'#444', textTransform:'uppercase', marginTop:'4px' }}>ZOOM</div>
          <button style={{ width:'24px', height:'24px', border:'1px solid #333', background:'#1a1a1a', color:'#888', borderRadius:0, cursor:'pointer', fontSize:'12px' }}>+</button>
          <button style={{ width:'24px', height:'24px', border:'1px solid #333', background:'#1a1a1a', color:'#888', borderRadius:0, cursor:'pointer', fontSize:'12px' }}>−</button>
        </div>
      )}
      </div>
    </Layout>
  );
}
